import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  OnInit,
  OnDestroy,
  forwardRef,
  ViewContainerRef,
  EmbeddedViewRef,
  ApplicationRef,
} from '@angular/core';
import { Observable, Subject, debounceTime, switchMap, catchError, of, tap, takeUntil } from 'rxjs';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { CAUserDto } from 'src/core/models/administration/user/CAUser.dto';
import { UserService } from 'src/ui/user-selector/services/user.service';
import { Operators } from 'src/core/models/request/operator.enum';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ObjectHelper } from 'src/core/helpers/object.helper';
import { CommentWithMentionInputModel } from '../models/comment-with-mention-input.model';
import { AutoUnsubscribe } from 'src/core/decorators/auto-unsubscribe.decorator';
import { HasSubscription } from 'src/ca-shared/has-subscription/has-subscription';

@Component({
  selector: 'ca-text-area-with-mention',
  templateUrl: './text-area-with-mention.component.html',
  styleUrl: './text-area-with-mention.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TextAreaWithMentionComponent),
    },
  ],
  standalone: false,
})
@AutoUnsubscribe()
export class TextAreaWithMentionComponent
  extends HasSubscription
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input()
  itemTemplate?: TemplateRef<any>;

  @Input()
  placeholder = '';

  @Input()
  appendToBody = false;

  @Input()
  minHeight: string;

  @Input()
  maxHeight: string;

  @Input()
  pageSize = 10;

  @Output()
  mentionSelected = new EventEmitter<CAUserDto>();

  @Output()
  textChange = new EventEmitter<string>();

  @Output()
  dropdownOpened = new EventEmitter<boolean>();

  @ViewChild('editorContainer')
  editorContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('dropdownTemplate', { read: TemplateRef })
  dropdownTemplate!: TemplateRef<any>;

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: false,
    height: 'inherit',
    minHeight: 'inherit',
    maxHeight: 'inherit',
    width: 'inherit',
    minWidth: '0',
    translate: 'no',
    enableToolbar: false,
    showToolbar: false,
    placeholder: this.placeholder,
    defaultParagraphSeparator: '',
    sanitize: false,
  };

  htmlContent = '';
  showDropdown = false;
  isLoading = false;
  filteredUsers: ListResponseDto<CAUserDto> = { items: [], totalCount: 0 };
  selectedIndex = 0;
  mentionStartIndex = -1;
  currentQuery = '';
  pageIndex = 0;
  totalCount = 0;
  isDisabled: boolean = false;

  dropdownPosition = { top: 0, left: 0 };
  dropdownElement?: HTMLElement;
  private embeddedViewRef?: EmbeddedViewRef<any>;

  taggedUsers = new Map<string, CAUserDto>();

  set value(val: CommentWithMentionInputModel) {
    this.taggedUsers.clear();
    val.selectedUsers.forEach(user => {
      this.taggedUsers.set(user.id, user);
    });
    this.htmlContent = val.htmlContent;
  }

  get value(): CommentWithMentionInputModel {
    return {
      selectedUsers: this.getTaggedUsersArray(),
      htmlContent: this.htmlContent,
    };
  }

  private searchSubject = new Subject<string>();

  private editorElement?: HTMLElement;
  private lastCursorPosition = 0;

  constructor(
    private service: UserService,
    private operators: Operators,
    private viewContainerRef: ViewContainerRef,
    private appRef: ApplicationRef,
  ) {
    super();
  }

  writeValue(obj: CommentWithMentionInputModel): void {
    obj = obj
      ? obj
      : {
          selectedUsers: [],
          htmlContent: '',
        };
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onChange(args: any): void {}

  onTouched(): void {}

  ngOnInit() {
    this.searchSubject
      .pipe(
        tap(() => {
          this.isLoading = true;
          this.filteredUsers = { items: [], totalCount: 0 };
          this.pageIndex = 0;
        }),
        debounceTime(300),
        switchMap(query =>
          query.length >= 0
            ? this.getFilteredUsers(query, this.pageIndex, this.pageSize).pipe(
                catchError(() => of({ items: [], totalCount: 0 })),
              )
            : of({ items: [], totalCount: 0 }),
        ),
        takeUntil(this.autoUnsubscribeNotifier),
      )
      .subscribe(users => {
        this.filteredUsers = users;
        this.totalCount = users.totalCount;
        this.selectedIndex = 0;
        this.isLoading = false;
      });

    this.editorConfig.placeholder = this.placeholder;
    this.editorConfig.maxHeight = this.maxHeight ? this.maxHeight : 'inherit';
    this.editorConfig.minHeight = this.minHeight ? this.minHeight : 'inherit';

    if (this.minHeight) {
      this.editorConfig.height = this.minHeight;
    }
  }

  ngOnDestroy() {
    this.removeDropdownFromBody();
    this.destroyDropdownView();
  }

  onContentChange(content: string) {
    this.htmlContent = content;
    this.validateTaggedUsers();

    // Update cursor position before detecting mention
    setTimeout(() => {
      this.updateCursorPosition();
      this.detectMention();
    }, 10);

    this.textChange.emit(this.getPlainText());
    this.applyChanges();
  }

  getPlainText(): string {
    const div = document.createElement('div');
    div.innerHTML = this.htmlContent;
    return div.textContent || div.innerText || '';
  }

  validateTaggedUsers() {
    const usersToRemove: string[] = [];

    this.taggedUsers.forEach((taggedUser, id) => {
      const mentionPattern = `"${id}(.*)${taggedUser.fullname}`;
      if (!new RegExp(mentionPattern).test(this.htmlContent)) {
        usersToRemove.push(id);
      }
    });

    usersToRemove.forEach(id => this.taggedUsers.delete(id));
  }

  detectMention() {
    // Get the current cursor position and text content
    this.updateCursorPosition();
    const plainText = this.getPlainText();
    const cursorPos = this.lastCursorPosition;

    if (cursorPos <= 0) {
      this.changeDropdownVisibility(false);
      return;
    }

    const textBeforeCursor = plainText.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex === -1) {
      this.changeDropdownVisibility(false);
      return;
    }

    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
    const hasSpace = textAfterAt.includes(' ') || textAfterAt.includes('\n');

    if (hasSpace) {
      this.changeDropdownVisibility(false);
      return;
    }

    this.mentionStartIndex = lastAtIndex;
    this.currentQuery = textAfterAt;
    this.changeDropdownVisibility(true);
    this.isLoading = true;
    this.searchSubject.next(textAfterAt);
    this.calculateDropdownPosition();
  }

  calculateDropdownPosition() {
    const editorEl = this.getEditorElement();
    if (!editorEl) return;

    const containerRect = this.editorContainer?.nativeElement?.getBoundingClientRect();

    if (containerRect) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rects = range.getClientRects();
        if (rects.length > 0) {
          const rect = rects[0];

          if (this.appendToBody) {
            // Position relative to viewport
            this.dropdownPosition = {
              top: rect.bottom + window.scrollY,
              left: rect.left + window.scrollX - 15,
            };

            if (this.embeddedViewRef) {
              const domElem = this.embeddedViewRef.rootNodes[0] as HTMLElement;
              this.updateDropdownPosition(domElem);
            }
          } else {
            // Position relative to container
            this.dropdownPosition = {
              top: rect.top - containerRect.top + 20,
              left: rect.left - containerRect.left - 15,
            };
          }
        }
      }
    }
  }

  onPageChange(event: any) {
    this.pageIndex = event.pageIndex;
    this.isLoading = true;
    this.getFilteredUsers(this.currentQuery, this.pageIndex, this.pageSize)
      .pipe(catchError(() => of({ items: [], totalCount: 0 })))
      .subscribe(users => {
        this.filteredUsers = users;
        this.totalCount = users.totalCount;
        this.selectedIndex = 0;
        this.isLoading = false;
      });
  }

  onEditorKeyDown(event: KeyboardEvent) {
    if (event.shiftKey && event.key === 'Enter') {
      event.preventDefault();
      this.htmlContent += '<div><br></div>';
      setTimeout(() => {
        this.focusEditor();
        this.getEditorElement()?.scrollTo(0, this.getEditorElement()?.scrollHeight);
      }, 5);
    } else if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.showDropdown) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredUsers.items.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        break;
      case 'Enter':
        if (this.filteredUsers.items[this.selectedIndex]) {
          event.preventDefault();
          event.stopPropagation();
          this.selectUser(this.filteredUsers.items[this.selectedIndex]);
        }
        break;
      case 'Escape':
        this.changeDropdownVisibility(false);
        break;
    }
  }

  selectUser(user: CAUserDto) {
    // Work with HTML content to preserve existing mentions
    const mentionText = `@${this.currentQuery}`;
    const mentionHtml = `<span class="mention-tag" data-user-id="${user.id}" contenteditable="false">@${user.fullname}</span>`;

    // Replace the @ and query text in HTML content while preserving other HTML
    const updatedHtmlContent = this.replaceTextInHtml(
      this.htmlContent,
      mentionText,
      mentionHtml + '&nbsp;',
      this.mentionStartIndex,
    );

    this.htmlContent = updatedHtmlContent;
    this.taggedUsers.set(user.id, user);

    this.changeDropdownVisibility(false);
    this.mentionSelected.emit(user);

    // Emit text change
    setTimeout(() => {
      this.textChange.emit(this.getPlainText());
      this.focusEditor();
      this.applyChanges();
    }, 100);
  }

  getTaggedUsersArray(): CAUserDto[] {
    return Array.from(this.taggedUsers.values());
  }

  getTaggedUsersMap(): Map<string, CAUserDto> {
    return this.taggedUsers;
  }

  applyChanges() {
    this.onChange(ObjectHelper.deepCopy(this.value));
  }

  private focusEditor() {
    const editorEl = this.getEditorElement();
    if (editorEl) {
      editorEl.focus();
      // Try to position cursor at the end
      try {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(editorEl);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      } catch (e) {
        // Ignore cursor positioning errors
      }
    }
  }

  private removeDropdownFromBody() {
    if (this.dropdownElement && this.dropdownElement.parentNode) {
      this.dropdownElement.parentNode.removeChild(this.dropdownElement);
      this.dropdownElement = undefined;
    }
  }

  private updateCursorPosition() {
    try {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        const editorEl = this.getEditorElement();
        if (editorEl) {
          preCaretRange.selectNodeContents(editorEl);
          preCaretRange.setEnd(range.endContainer, range.endOffset);
          this.lastCursorPosition = preCaretRange.toString().length;
        }
      }
    } catch (e) {
      // Fallback: estimate position from plain text length
      this.lastCursorPosition = this.getPlainText().length;
    }
  }

  private replaceTextInHtml(
    htmlContent: string,
    searchText: string,
    replaceHtml: string,
    startIndex: number,
  ): string {
    // Create a temporary div to work with the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Get all text nodes
    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null);

    let currentIndex = 0;
    let targetNode: Node | null = null;
    let targetOffset = -1;

    // Find the text node that contains our target position
    let node;
    while ((node = walker.nextNode())) {
      const nodeLength = node.textContent?.length || 0;
      if (currentIndex + nodeLength > startIndex) {
        targetNode = node;
        targetOffset = startIndex - currentIndex;
        break;
      }
      currentIndex += nodeLength;
    }

    if (targetNode && targetNode.textContent) {
      const textContent = targetNode.textContent;
      const beforeText = textContent.substring(0, targetOffset);
      const afterText = textContent.substring(targetOffset + searchText.length);

      // Create new content
      const newContent = beforeText + '{{MENTION_PLACEHOLDER}}' + afterText;
      targetNode.textContent = newContent;

      // Replace placeholder with HTML
      const finalHtml = tempDiv.innerHTML.replace('{{MENTION_PLACEHOLDER}}', replaceHtml);
      return finalHtml;
    }

    return htmlContent;
  }

  private getEditorElement(): HTMLElement | null {
    if (!this.editorElement) {
      this.editorElement = this.editorContainer?.nativeElement?.querySelector(
        '.angular-editor-textarea',
      ) as HTMLElement;
    }
    return this.editorElement;
  }

  private getFilteredUsers(
    query: string,
    pageIndex: number = 0,
    pageSize: number = 10,
  ): Observable<ListResponseDto<CAUserDto>> {
    return this.service.getList(
      [
        {
          field: 'quickSearchTerm',
          value: query,
          operator: this.operators.Contains,
        },
      ],
      pageIndex,
      pageSize,
      true,
      true,
    );
  }

  private changeDropdownVisibility(isVisible: boolean) {
    const wasVisible = this.showDropdown;
    this.showDropdown = isVisible;
    this.dropdownOpened.emit(isVisible);

    if (this.appendToBody) {
      if (isVisible && !wasVisible) {
        setTimeout(() => this.createDropdownView(), 0);
      } else if (!isVisible && wasVisible) {
        this.destroyDropdownView();
      }
    }
  }

  private createDropdownView() {
    if (!this.showDropdown || !this.appendToBody || !this.dropdownTemplate) return;

    // Destroy any existing view
    this.destroyDropdownView();

    // Create embedded view from template
    this.embeddedViewRef = this.viewContainerRef.createEmbeddedView(this.dropdownTemplate);

    // Attach to application
    try {
      this.appRef.attachView(this.embeddedViewRef);
    } catch (e) {
      // Ignore if already attached
    }

    // Get DOM element
    const domElem = this.embeddedViewRef.rootNodes[0] as HTMLElement;

    // Append to body
    document.body.append(domElem);

    // Apply positioning
    this.updateDropdownPosition(domElem);
  }

  private updateDropdownPosition(element: HTMLElement) {
    element.style.position = 'fixed';
    element.style.top = `${this.dropdownPosition.top}px`;
    element.style.left = `${this.dropdownPosition.left}px`;
    element.style.zIndex = '9999';
  }

  private destroyDropdownView() {
    if (this.embeddedViewRef) {
      this.appRef.detachView(this.embeddedViewRef);
      this.embeddedViewRef.destroy();
      this.embeddedViewRef = undefined;
    }
  }
}
